// covered-by: frontends/carrie/src/ask-v2/rail/__tests__/AskV2RightRail.test.tsx
import { CaretDown } from "@phosphor-icons/react";
import { Text } from "@radix-ui/themes";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { type AskV2RailSectionId } from "./types";

// why: closed header is 32px tall — 16px radius is a lozenge, 12px is not
export const ASK_V2_RAIL_SECTION_CLASS =
  "flex min-w-0 shrink-0 flex-col overflow-hidden rounded-2xl border border-puntt-neutral-gray-5 bg-base-white";

export const ASK_V2_RAIL_TOGGLE_CLASS =
  "flex h-8 w-full shrink-0 items-center gap-2 bg-base-white px-4 py-2 text-left";

export const ASK_V2_RAIL_TOGGLE_TITLE_CLASS =
  "min-w-0 flex-1 text-puntt-neutral-grayA-11";

// why: same bottom fade as the review comment list, tinted to this card's white fill
export const ASK_V2_RAIL_SCROLL_FADE_CLASS =
  "pointer-events-none absolute inset-x-0 bottom-0 z-10 h-5 bg-[linear-gradient(to_top,rgb(var(--base-white))_15%,transparent)]";

type AskV2RailSectionProps = {
  id: AskV2RailSectionId;
  title: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: ReactNode;
};

export function AskV2RailSection({
  id,
  title,
  open,
  onOpenChange,
  children,
}: AskV2RailSectionProps) {
  const { t } = useTranslation("ui");
  const bodyRef = useRef<HTMLDivElement>(null);
  const [canScrollMore, setCanScrollMore] = useState(false);

  useEffect(() => {
    const el = bodyRef.current;
    if (!open || !el) return;
    const measure = () => {
      const remaining = el.scrollHeight - el.scrollTop - el.clientHeight;
      setCanScrollMore(remaining > 1);
    };
    measure();
    el.addEventListener("scroll", measure, { passive: true });
    const mutation = new MutationObserver(measure);
    mutation.observe(el, {
      childList: true,
      subtree: true,
      characterData: true,
    });
    const resize =
      typeof ResizeObserver === "undefined"
        ? undefined
        : new ResizeObserver(measure);
    resize?.observe(el);
    return () => {
      el.removeEventListener("scroll", measure);
      mutation.disconnect();
      resize?.disconnect();
    };
  }, [open]);

  return (
    <section
      className={ASK_V2_RAIL_SECTION_CLASS}
      aria-label={title}
      data-testid={`ask-v2-rail-section-${id}`}
      data-panel-expanded={open ? "true" : "false"}
    >
      <button
        type="button"
        onClick={() => onOpenChange(!open)}
        aria-expanded={open}
        aria-label={t("askV2.rail.toggleSection", { section: title })}
        data-testid={`ask-v2-rail-toggle-${id}`}
        className={ASK_V2_RAIL_TOGGLE_CLASS}
      >
        <Text
          as="span"
          size="1"
          weight="medium"
          className={ASK_V2_RAIL_TOGGLE_TITLE_CLASS}
        >
          {title}
        </Text>
        <CaretDown
          size={14}
          aria-hidden="true"
          className={`shrink-0 text-puntt-neutral-gray-11 transition-transform ${open ? "" : "-rotate-90"}`}
        />
      </button>
      {open ? (
        <div className="relative">
          <div
            ref={bodyRef}
            className="max-h-[min(50vh,420px)] min-w-0 overflow-y-auto overscroll-contain border-t border-puntt-neutral-gray-5 px-4 pb-4 pt-3 [scrollbar-width:thin]"
            data-testid={`ask-v2-rail-body-${id}`}
          >
            {children}
          </div>
          {canScrollMore ? (
            <div
              aria-hidden="true"
              className={ASK_V2_RAIL_SCROLL_FADE_CLASS}
              data-testid={`ask-v2-rail-body-${id}-fade`}
            />
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
